/*
    A single radio content style view element
*/
import View from '@ckeditor/ckeditor5-ui/src/view';
import uid from '@ckeditor/ckeditor5-utils/src/uid';

export default class ContentStyleRadio extends View {
	/*
	 * @param {Locale} locale
	 * @param {ContentStyleModel} style
	 */
	constructor( locale, style ) {
		super( locale );

		const inputUid = `ck-input-${ uid() }`;

		/**
		 * The value of the input.
		 *
		 * @observable
		 * @member {String} #value
		 */
		this.set( 'value' );

		/**
		 * The name of the input
		 *
		 * @observable
		 * @member {String} #value
		 */
		this.set( 'name' );

		/**
		 * The checked state
		 *
		 * @observable
		 * @member {String} #value
		 */
		this.set( 'checked' );

		/**
		 * Controls whether the radio view is visible
		 *
		 * @observable
		 * @member {String} #value
		 */
		this.set( 'isHidden', true );

		/**
		 * The image preview
		 *
		 * @observable
		 * @member {String} #value
		 */
		this.set( 'preview' );

		/**
		 * The data image attribute
		 *
		 * @observable
		 * @member {String} #value
		 */
		this.set( 'data-image-path' );

		/**
		 * The data image attribute
		 *
		 * @observable
		 * @member {String} #value
		 */
		this.set( 'data-image-alt' );

		/**
		 * The data image attribute
		 *
		 * @observable
		 * @member {String} #value
		 */
		this.set( 'data-image-class' );

		/**
		 * The style object used to create the radio option.
		 *
		 * @observable
		 * @member {ContentStyleModel} #style
		 */
		this.set( 'style' );

		// Set the initial state off of the style object.
		this.style = style;
		this.isHidden = style.isHidden;
		this.value = style.classes;
		this.preview = style.preview;

		this.inputView = this._createInputView( inputUid );
		this.labelView = this._createLabelView( inputUid );

		const bind = this.bindTemplate;
		this.setTemplate( {
			tag: 'li',
			children: [ this.inputView, this.labelView ],
			attributes: {
				style: [ bind.if( 'isHidden', 'display:none' ) ]
			}
		} );
	}

	render() {
		super.render();

		// Set the initial state.
		const radio = this.element.firstElementChild;
		radio.checked = !!this.value;

		// Bind `this.value` to the DOM element's checked state.
		this.on( 'change:checked', ( evt, name, value ) => {
			const radio = this.element.firstElementChild;
			radio.checked = !!value;
		} );
		// CMS9 added input event.
		this.on( 'input', () => {
			const radio = this.element.firstElementChild;
			this.checked = radio.checked;
		} );
	}

	/**
	 * Update the state based on the style object.
	 */
	refresh() {
		this.isHidden = this.style.isHidden;
	}

	/**
	 * Creates input radio view class instance and bind with view.
	 */
	_createInputView( id ) {
		const radioView = new View( this.locale );
		const bind = this.bindTemplate;

		radioView.setTemplate( {
			tag: 'input',
			attributes: {
				type: 'radio',
				name: bind.to( 'name' ),
				id,
				value: bind.to( 'value' ),
				'data-image-path': bind.to( 'data-image-path' ),
				'data-image-alt': bind.to( 'data-image-alt' ),
				'data-image-class': bind.to( 'data-image-class' )
			},
			on: {
				input: bind.to( 'input' )
			}
		} );

		return radioView;
	}

	/**
	 * Creates label view class instance and bind with view.
	 */
	_createLabelView( id ) {
		const labelView = new View( this.locale );
		const imgPreview = this._createImagePreview();
		labelView.setTemplate( {
			tag: 'label',
			attributes: {
				for: id
			},
			children: [ imgPreview ]
		} );

		return labelView;
	}

	_createImagePreview() {
		const imgView = new View( this.locale );
		const bind = this.bindTemplate;

		imgView.setTemplate( {
			tag: 'img',
			attributes: {
				src: bind.to( 'preview' )
			}
		} );

		return imgView;
	}
}
